import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { Ruler3D } from '../decor/Ruler3D'
import { TankDecorGroup3D } from '../decor/TankDecorGroup3D'
import { FeatureDispatch } from '../features/FeatureDispatch'
import {
  planeGeometry,
  xWrappedShellGeometry,
} from '@asset-viz/utils/geometryDefines'
import { extractScale, Vec3 } from '@asset-viz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoAssetModel,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoPressureVesselShell,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing a pressure vessel shell - a cylindrical component
 * with pressure vessel specific features like nozzles, manholes, and welds.
 *
 * @param model       The pressure vessel shell component model
 * @param assetModel  The asset model containing this shell
 * @param groupProps  Position, rotation, scale, and other 3D properties
 */
export const PressureVesselShellSurface3D: React.FC<
  {
    model: GeckoPressureVesselShell
    assetModel?: GeckoAssetModel
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const {
    bounds,
    scale,
    manways,
    rectangularManways,
    nozzles,
    plates,
    verticalWelds,
    horizontalWelds,
    seamWelds,
    circumferentialWelds,
  } = React.useMemo(() => {
    const bounds = model.componentBounds

    // For pressure vessel shell, wrap around X axis (circumferentially) like pipes
    const scale = extractScale(groupProps.scale).multiply(
      show3D
        ? new Vector3(
            model.radius.num, // radius
            model.componentBounds.height.num, // length
            model.radius.num // radius
          )
        : new Vector3(
            model.componentBounds.width.num,
            model.componentBounds.height.num,
            1.0
          )
    )

    return {
      bounds,
      scale,
      manways: model.manwaysArray,
      rectangularManways: model.rectangularManwaysArray,
      nozzles: model.nozzlesArray,
      verticalWelds: model.verticalWeldsArray,
      horizontalWelds: model.horizontalWeldsArray,
      plates: model.platesArray,
      seamWelds: model.seamWeldsArray,
      circumferentialWelds: model.circumferentialWeldsArray,
    }
  }, [model, groupProps.scale, show3D])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    return xWrappedShellGeometry // Wraps around X axis like pipes
  }, [show3D])

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(bounds.start.x.unit)
        .minus(bounds.start.x)
        .scale(1.0 / bounds.width.num).num
      if (!show3D) return [relX, 0, 0]
      const angle = 2.0 * Math.PI * (relX - 0.25) // rotated by 90 degrees in 3D mode
      return [Math.sin(angle), 0, Math.cos(angle)]
    },
    [show3D, bounds]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(bounds.start.y)
        .scale(1.0 / bounds.height.num).num
      if (!show3D) return [0, relY, 0]
      return [-0.875, relY, 0.5]
    },
    [show3D, bounds]
  )

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}>
      <FeatureDispatch
        features={[
          manways,
          rectangularManways,
          nozzles,
          plates,
          verticalWelds,
          horizontalWelds,
          seamWelds,
          circumferentialWelds,
        ]}
      />
      {/* X ruler ---------------------------------------------------------- */}
      <TankDecorGroup3D>
        <Ruler3D
          anchor={'right'}
          start={model.componentBounds.start.x}
          end={model.componentBounds.end.x}
          includeTicks
          includeStart
          includeEnd={!show3D}
          getTickPosition={positionXTick}
        />
      </TankDecorGroup3D>
      {/* Y ruler -------------------------------------------------------- */}
      <TankDecorGroup3D yPivot={show3D}>
        <Ruler3D
          anchor={'bottom'}
          start={model.componentBounds.start.y}
          end={model.componentBounds.end.y}
          includeTicks
          includeStart
          includeEnd
          getTickPosition={positionYTick}
        />
      </TankDecorGroup3D>
    </ModelSurface3D>
  )
}
